/** Uses the existing confirmation screen with the direct study's saved source. */
import { useQuery } from '@tanstack/react-query'
import { shopifyQueryPolicies } from '../../queries/shopifyQueryPolicies'
import { readDirectBuilderSource } from '../../hooks/study-builder/studyAuthoringBootstrap'
import { DirectStudyDeletion } from './DirectStudyDeletion'
import type { ShopifyAppBridge } from '../../shopifyApiFetch'
import { useShopifyAppContext } from '../../ShopifyAppContext'
import { buildStudyBuilderViewUrl } from '../../resultsNavigation'
import { resolveMerchantErrorMessage } from '../../merchantErrors'
import { StudyRunPage } from '../StudyRunPage'
import { ShopifyBackActionButton } from '../ShopifyPageActionButtons'
import { trackShopifyAction, trackShopifyRefetch } from '../../shopifyUiTracking'
export function DirectStudyRunRoute({
  shopify,
  studyId,
  onBack,
}: {
  shopify: ShopifyAppBridge
  studyId: string
  onBack: () => void
}) {
  const scope = useShopifyAppContext()
  const read = useQuery({
    ...shopifyQueryPolicies.studyAuthoring,
    queryKey: ['direct-study-run', scope.shopDomain, scope.orgId, scope.connectionId, studyId],
    queryFn: ({ signal }) => readDirectBuilderSource(shopify, { studyId }, signal),
    retry: false,
    // The server says how often a moving status (a started run) must be re-read; null once it settles.
    refetchInterval: (query) => query.state.data?.authoring?.state.pollMs ?? false,
  })
  if (read.data?.deletion) return <DirectStudyDeletion shopify={shopify} receipt={read.data.deletion} onBack={onBack} />
  let body = (
    <StudyRunPage
      key={studyId}
      shopify={shopify}
      studyId={studyId}
      authoring={read.data?.authoring?.authoring}
      authoringState={read.data?.authoring?.state}
      onRunStarted={() => void read.refetch()}
      isLoadingSetup={read.isPending}
      onBackToStudies={onBack}
    />
  )
  if (read.isError)
    body = (
      <s-banner tone="critical">
        <s-text>{resolveMerchantErrorMessage(read.error, 'The test could not be loaded.')}</s-text>
        <s-button onClick={() => void trackShopifyRefetch(shopify, () => read.refetch())}>Try again</s-button>
      </s-banner>
    )
  return (
    <s-page heading="Confirm & run" inlineSize="base">
      <ShopifyBackActionButton action={{ content: 'Tests', onAction: onBack }} />
      {read.data?.authoring?.state.canEdit && (
        <s-button
          slot="secondary-actions"
          href={buildStudyBuilderViewUrl({ studyId })}
          onClick={() => trackShopifyAction(shopify, 'open-editor', () => undefined)}
        >
          Edit test
        </s-button>
      )}
      {body}
    </s-page>
  )
}
